<template>
  <form class="space-y-6" @submit.prevent="submit">
    <div class="text-center mb-8 lg:hidden">
      <!-- Logic to hide title if header is present in layout, but added here for completeness context if needed -->
      <h2 class="text-2xl font-bold text-gray-900">Welcome Back</h2>
      <p class="text-sm text-gray-500 mt-1">Please sign in to your account</p>
    </div>

    <!-- Header for Desktop (matches new Layout flow) -->
    <div class="hidden lg:block mb-8">
      <h2 class="text-3xl font-bold text-gray-900 tracking-tight">Welcome Back</h2>
      <p class="text-gray-500 mt-2">Enter your credentials to access your workspace.</p>
    </div>

    <div>
      <label for="email" class="block text-sm font-semibold text-gray-700 mb-2">
        Email Address
      </label>
      <div class="relative group">
        <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200 group-focus-within:text-green-600">
          <FontAwesomeIcon icon="envelope" class="h-5 w-5 text-gray-400 group-focus-within:text-green-500" />
        </div>
        <input
          id="email"
          v-model="email"
          type="email"
          required
          class="block w-full pl-12 pr-4 py-4 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 placeholder-gray-400 transition-all duration-300 font-medium text-gray-900"
          placeholder="name@company.com"
        />
      </div>
    </div>

    <div>
      <div class="flex justify-between items-center mb-2">
         <label for="password" class="block text-sm font-semibold text-gray-700">
          Password
        </label>
        <RouterLink
          to="/forgot-password"
          class="text-sm font-medium text-green-600 hover:text-green-500 transition-colors"
        >
          Forgot password?
        </RouterLink>
      </div>
      <div class="relative group">
        <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200">
          <FontAwesomeIcon icon="lock" class="h-5 w-5 text-gray-400 group-focus-within:text-green-500" />
        </div>
        <input
          id="password"
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          required
          class="block w-full pl-12 pr-12 py-4 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 placeholder-gray-400 transition-all duration-300 font-medium text-gray-900"
          placeholder="••••••••"
        />
        <button
          type="button"
          @click="showPassword = !showPassword"
          class="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        >
          <FontAwesomeIcon :icon="showPassword ? 'eye-slash' : 'eye'" class="h-5 w-5" />
        </button>
      </div>
    </div>

    <div class="flex items-center">
      <input
        id="remember-me"
        v-model="rememberMe"
        type="checkbox"
        class="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer transition-all duration-200"
      />
      <label for="remember-me" class="ml-2 block text-sm text-gray-600 cursor-pointer select-none">
        Keep me signed in
      </label>
    </div>

    <div class="pt-2">
      <button
        type="submit"
        :disabled="isLoading"
        class="w-full flex justify-center items-center py-4 px-4 rounded-xl bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white font-bold text-lg shadow-lg hover:shadow-green-500/30 transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
      >
        <template v-if="isLoading">
          <FontAwesomeIcon icon="circle-notch" spin class="mr-2 h-5 w-5" />
          Signing in...
        </template>
        <template v-else>
          Sign In
        </template>
      </button>
    </div>

    <div class="text-center mt-6">
      <p class="text-sm text-gray-500">
        Don't have an account yet? 
        <RouterLink to="/register" class="font-bold text-green-600 hover:text-green-500 transition-colors">
          Create Account
        </RouterLink>
      </p>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faEnvelope, faLock, faEye, faEyeSlash, faCircleNotch } from '@fortawesome/free-solid-svg-icons'

library.add(faEnvelope, faLock, faEye, faEyeSlash, faCircleNotch)

const emit = defineEmits<{
  (e: 'submit', credentials: { email: string; password: string }): void
}>()

const email = ref('')
const password = ref('')
const rememberMe = ref(false)
const isLoading = ref(false)
const showPassword = ref(false)

const submit = async () => {
  isLoading.value = true
  try {
    await emit('submit', {
      email: email.value,
      password: password.value
    })
  } finally {
    isLoading.value = false
  }
}
</script>
